import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown'
import type { DocumentNode } from 'graphql'

import {
    replaceForSuperScript,
    replaceForSymbol,
} from '@components/markdown/utils'
import {
    CUSTOM_IMAGE,
    SANITIZED_HTML,
} from '@contentful-components/rich-text/render'
import type { ContentfulClient } from '@graphql/client'
import type { PolicyFragment } from '@graphql/fragments/PolicyFragment.graphql'
import type { PolicySectionFragment } from '@graphql/fragments/PolicySectionFragment.graphql'
import { requestQuery } from '@graphql/functions'
import type { PolicyCategoryFragment } from '@graphql/queries/GetPolicies.graphql'
import { GetPolicyContentDocument } from '@graphql/queries/GetPolicyContent.graphql'
import { GetTextDocument } from '@graphql/queries/GetText.graphql'
import { compact } from '@lib/utils/omit'

// Type for mutable rich text nodes that can be transformed
type RichTextNode = {
    nodeType: string
    value?: string
    content?: RichTextNode[]
    marks?: unknown[]
    data?: Record<string, unknown>
}

type PolicySectionFragmentType = NonNullable<
    PolicyFragment['sections']
>['items'][0]['type']

const typeToQueryMap: Partial<Record<PolicySectionFragmentType, DocumentNode>> =
    {
        PolicyContent: GetPolicyContentDocument,
        Text: GetTextDocument,
    }

export const categoryHasPolicies = (
    category: Omit<PolicyCategoryFragment, 'type' | 'sys'>,
): boolean => !!category?.policies?.items?.length

export const getEnrichedPolicySections = async (
    client: ContentfulClient,
    sections: PolicySectionFragment[],
) => {
    const mappedRequests = sections.map((section) => {
        const query = typeToQueryMap[section.type]

        if (!query) {
            throw new Error(`No query found for type ${section.type}`)
        }

        return requestQuery(client, {
            query,
            variables: { id: section.sys.id },
        }).then(async (item: any) => {
            if ('text' in item?.data) {
                // return content already in rich text format, but process it for symbols/superscripts
                const compactedContent = compact(item.data.text.content, true)

                // Process the rich text content for symbol and superscript replacements
                if (compactedContent?.json) {
                    processRichTextNodes(compactedContent.json)
                }

                return {
                    ...item.data.text,
                    content: compactedContent,
                }
            }
            if ('policyContent' in item?.data) {
                // convert markdown to rich text format
                const json = await policyMarkdownToRichText(
                    item.data.policyContent.body,
                )

                return {
                    ...item.data.policyContent,
                    content: {
                        json,
                        links: {
                            entries: [],
                        },
                    },
                }
            }
        })
    })

    const resolvedSections = await Promise.all(mappedRequests)

    return {
        sections: resolvedSections,
    }
}

export const policyMarkdownToRichText = async (markdown: string) => {
    const result = await richTextFromMarkdown(
        markdown,
        async (markdownNode) => {
            switch (markdownNode.type) {
                case 'image': {
                    const { url, alt } = markdownNode as any
                    return {
                        nodeType: CUSTOM_IMAGE,
                        content: [],
                        data: {
                            src: url,
                            alt,
                        },
                    }
                }

                default:
                    return null
            }
        },
    )

    // Post-process the rich text AST to handle superscript and symbol replacements
    processRichTextNodes(result)

    return result
}

/**
 * Recursively processes rich text nodes to apply superscript and symbol transformations
 * Exported for use in other modules
 */

export const processRichTextNodes = (node: RichTextNode): void => {
    if (!node) return

    // Process text nodes
    if (node.nodeType === 'text' && typeof node.value === 'string') {
        const text = node.value

        // Apply all transformations (the functions handle cases where patterns don't match)
        let transformedText = replaceForSuperScript(text)
        transformedText = replaceForSymbol(transformedText)

        // Replace the text node with a sanitized HTML node if transformations were applied
        if (transformedText !== text) {
            node.nodeType = SANITIZED_HTML
            node.content = []
            node.data = { html: transformedText }
            delete node.value
            delete node.marks
        }
    }

    // Recursively process content nodes
    if (Array.isArray(node.content)) {
        node.content.forEach(processRichTextNodes)
    }
}

// section is subsection if title has any number of characters after the first period before the first space
// example: "III. hello" is not a subsection, "IV.a hello" is a subsection
export const checkSubsection = (title: string): boolean => {
    return !!title?.match(/^[a-zA-Z0-9]+\.[a-zA-Z0-9]+\s/)
}
